import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography, Table, Select, Input, Button, DatePicker, Space, Tooltip, message, Timeline, Tag, Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SearchOutlined, DashboardOutlined, LockOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { FctCard, FctEstadoTag, FctListHeader } from '../estilo-corporativo-fct';
import {
  getFirmas, getCronologia, FdocFirmaEstado, FdocFirmaCronologiaEvento, SeguimientoFdocFiltro,
  ESTADOS_RECORRIDO, ETIQUETAS_INCIDENCIA,
} from '../api/seguimientoFdocApi';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Panel «Seguimiento de documentos en firma digital» (FDoc + Signaturit).
 *
 * Pinta lo que `VW_FDOC_FIRMA_ESTADO` devuelve (estado, tipo de documento, responsable, avance por
 * personas) y, al desplegar una fila, la cronología evento a evento de `VW_FDOC_FIRMA_CRONOLOGIA` —
 * esta última SOLO para el perfil de Sistemas, porque lleva nombre y correo de los firmantes
 * (clientes). El backend la niega por rol con 403; el front no oculta nada "por si acaso", muestra
 * el 403 como lo que es.
 */

/** Colores por estado. Los de incidencia van todos en rojo: son un final, no un punto del recorrido. */
const COLOR_ESTADO: Record<string, string> = {
  pendiente: 'default',
  enviado: 'blue',
  entregado: 'cyan',
  abierto: 'gold',
  firmado: 'green',
  completado: 'success',
};

/** Colores del Timeline por punto del recorrido (1 enviado … 5 completado). */
const COLOR_ORDEN: Record<number, string> = {
  1: 'blue',
  2: 'cyan',
  3: 'gold',
  4: 'green',
  5: 'green',
};

/** Etiqueta legible de un estado, venga del recorrido o sea una incidencia. */
const etiquetaEstado = (estado: string): string =>
  ETIQUETAS_INCIDENCIA[estado] ?? estado.charAt(0).toUpperCase() + estado.slice(1);

/** Etiqueta legible de un punto del recorrido (NU_ORDEN_HITO de la cronología). */
const ETIQUETAS_ORDEN: Record<number, string> = {
  1: 'Enviado', 2: 'Entregado', 3: 'Abierto', 4: 'Firmado', 5: 'Completado',
};
const etiquetaOrden = (ordenHito: number, tipo: string): string => ETIQUETAS_ORDEN[ordenHito] ?? tipo;

/**
 * Las fechas llegan en UTC pero sin sufijo Z (DateTime de .NET con Kind=Unspecified). Sin esto,
 * el navegador las leería como hora local y todo el panel iría desfasado una o dos horas.
 */
const fechaUtc = (iso?: string | null): Dayjs | null => {
  if (!iso) return null;
  return dayjs(/(Z|[+-]\d{2}:\d{2})$/.test(iso) ? iso : `${iso}Z`);
};

const formatear = (iso?: string | null): string => fechaUtc(iso)?.format('DD/MM/YYYY HH:mm') ?? '—';

/** Estado de la cronología de una fila: sin pedir todavía, cargando, cargada, prohibida o fallida. */
type EstadoCronologia =
  | { tipo: 'cargando' }
  | { tipo: 'ok'; eventos: FdocFirmaCronologiaEvento[] }
  | { tipo: 'prohibido' }
  | { tipo: 'error' };

/**
 * Cronología de UN destinatario (un destinatario = un ID_DOCUMENTO del sobre, o su email/orden
 * cuando todavía no tiene ID_DOCUMENTO porque no ha recibido ningún evento — ver más abajo).
 *
 * El recorrido va en VERTICAL, que es como se lee una línea de tiempo. Lo que va en
 * horizontal son los destinatarios: una columna por persona, en vez de apilarlos uno
 * debajo de otro — con dos firmantes, apilar hacía que cada fila desplegada ocupara
 * media pantalla.
 */
const CronologiaDestinatario: React.FC<{ eventos: FdocFirmaCronologiaEvento[] }> = ({ eventos }) => {
  // Solo cuentan como "evento" real las filas que traen tipo: la firma es secuencial y un
  // destinatario que aún no ha recibido nada llega con TODAS sus columnas de evento a NULL.
  const hitos = eventos.filter((e) => e.tipo && !e.incidencia && e.ordenHito > 0).sort((a, b) =>
    (fechaUtc(a.fechaEvento)?.valueOf() ?? 0) - (fechaUtc(b.fechaEvento)?.valueOf() ?? 0));
  const incidencias = eventos.filter((e) => e.tipo && e.incidencia).sort((a, b) =>
    (fechaUtc(a.fechaEvento)?.valueOf() ?? 0) - (fechaUtc(b.fechaEvento)?.valueOf() ?? 0));
  const enEspera = hitos.length === 0 && incidencias.length === 0;

  const primero = eventos[0];

  return (
    <div style={{ minWidth: 260, opacity: enEspera ? 0.55 : 1 }}>
      <div style={{ marginBottom: 6 }}>
        <Space size={6} wrap>
          <Text strong>{primero?.destinatario || 'Destinatario sin nombre'}</Text>
          {/* Firmante de la casa: siempre primero (ver ordenación de grupos) y marcado para no
              tener que mirar el dominio del correo para distinguirlo de un cliente. */}
          {primero?.esFct && <Tag color="blue">Farmaconsulting</Tag>}
        </Space>
        {primero?.email && <div><Text type="secondary">{primero.email}</Text></div>}
      </div>

      {enEspera ? (
        // Es lo normal en firma secuencial (hasta que no firma el anterior, a este no le llega
        // nada), no una anomalía: se pinta apagado, no como error ni como fila vacía.
        <Tag>En espera</Tag>
      ) : hitos.length > 0 ? (
        <Timeline
          items={hitos.map((h) => ({
            color: COLOR_ORDEN[h.ordenHito] ?? 'gray',
            children: (
              <span>
                <Text strong>{etiquetaOrden(h.ordenHito, h.tipo as string)}</Text>{' '}
                <Text type="secondary">— {formatear(h.fechaEvento)}</Text>
              </span>
            ),
          }))}
        />
      ) : (
        <Text type="secondary">Sin hitos de recorrido todavía.</Text>
      )}

      {/* Las incidencias (rechazado, caducado, cancelado, correo rebotado) son un FINAL, no un
          punto del recorrido: se pintan aparte y en rojo, fuera de la línea de tiempo. */}
      {incidencias.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {incidencias.map((i, idx) => (
            <div key={idx}>
              <Tag color="red">{ETIQUETAS_INCIDENCIA[i.tipo as string] ?? i.tipo}</Tag>
              <Text type="secondary">{formatear(i.fechaEvento)}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Ordena los destinatarios como se firma: la casa (Farmaconsulting) siempre delante, luego por
 * NU_ORDEN ascendente y, quien llegue sin orden (sobres anteriores a la tabla de destinatarios),
 * al final por la fecha de su primer evento. */
const compararDestinatarios = (
  a: FdocFirmaCronologiaEvento[], b: FdocFirmaCronologiaEvento[],
): number => {
  const ea = a[0];
  const eb = b[0];
  if (ea.esFct !== eb.esFct) return ea.esFct ? -1 : 1;
  if (ea.orden != null && eb.orden != null) return ea.orden - eb.orden;
  if (ea.orden != null) return -1;
  if (eb.orden != null) return 1;
  const fa = Math.min(...a.map((e) => fechaUtc(e.fechaEvento)?.valueOf() ?? Infinity));
  const fb = Math.min(...b.map((e) => fechaUtc(e.fechaEvento)?.valueOf() ?? Infinity));
  return fa - fb;
};

const CronologiaFila: React.FC<{ idRegistro: number; recarga: number }> = ({ idRegistro, recarga }) => {
  const [estado, setEstado] = useState<EstadoCronologia>({ tipo: 'cargando' });

  useEffect(() => {
    let cancelado = false;
    setEstado({ tipo: 'cargando' });
    getCronologia(idRegistro)
      .then((eventos) => { if (!cancelado) setEstado({ tipo: 'ok', eventos }); })
      .catch((err: unknown) => {
        if (cancelado) return;
        const status = err instanceof AxiosError ? err.response?.status : undefined;
        setEstado(status === 403 ? { tipo: 'prohibido' } : { tipo: 'error' });
      });
    return () => { cancelado = true; };
  }, [idRegistro, recarga]);

  if (estado.tipo === 'cargando') {
    return <Spin size="small" />;
  }

  if (estado.tipo === 'prohibido') {
    return (
      <Space>
        <LockOutlined />
        <Text type="secondary">
          La cronología con nombre y correo de los firmantes solo está disponible para el perfil de Sistemas.
        </Text>
      </Space>
    );
  }

  if (estado.tipo === 'error') {
    return <Text type="danger">No se pudo cargar la cronología. Vuelve a intentarlo.</Text>;
  }

  if (estado.eventos.length === 0) {
    return <Text type="secondary">Aún no ha llegado ningún evento de Signaturit para este documento.</Text>;
  }

  // Un destinatario = un ID_DOCUMENTO. Quien todavía no ha recibido nada (firma secuencial, en
  // espera) puede llegar sin ID_DOCUMENTO todavía, así que se agrupa por email/nombre/orden en
  // ese caso — nunca junto a otro destinatario distinto que también esté esperando.
  const grupos = new Map<string, FdocFirmaCronologiaEvento[]>();
  estado.eventos.forEach((e, idx) => {
    const clave = e.idDocumento ?? `sin-doc:${e.orden ?? 'x'}:${e.email ?? e.destinatario ?? idx}`;
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave)!.push(e);
  });

  // La firma es secuencial: hasta que no firma uno, al siguiente no le llega nada. Por eso la
  // fila no es "columnas sueltas" sino una SECUENCIA, ordenada de izquierda a derecha, con una
  // flecha entre destinatario y destinatario para que la cadena se lea de un vistazo.
  const destinatarios = Array.from(grupos.values()).sort(compararDestinatarios);

  return (
    // `wrap` para que en pantallas estrechas caigan debajo en vez de forzar scroll lateral
    // dentro de la fila desplegada; la flecha solo tiene sentido entre elementos consecutivos
    // del mismo renglón, así que se omite cuando el layout ya ha hecho un salto de línea propio
    // no es detectable en CSS puro — se acepta como límite conocido del wrap.
    <div style={{ padding: '8px 24px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 16 }}>
      {destinatarios.map((eventos, idx) => {
        const clave = eventos[0].idDocumento
          ?? `sin-doc:${eventos[0].orden ?? 'x'}:${eventos[0].email ?? eventos[0].destinatario ?? idx}`;
        return (
          <React.Fragment key={clave}>
            {idx > 0 && (
              <div style={{ alignSelf: 'center', paddingTop: 20 }}>
                <Text type="secondary" style={{ fontSize: 18 }}>→</Text>
              </div>
            )}
            <CronologiaDestinatario eventos={eventos} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

const SeguimientoFDocPage: React.FC = () => {
  const [filas, setFilas] = useState<FdocFirmaEstado[]>([]);
  const [loading, setLoading] = useState(false);

  const [estado, setEstado] = useState<string | undefined>();
  const [tipoDocumento, setTipoDocumento] = useState<string | undefined>();
  const [solicitanteOid, setSolicitanteOid] = useState<string | undefined>();
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [texto, setTexto] = useState('');
  /** Texto ya confirmado (Enter o borrado): el filtro va al servidor, no se dispara en cada tecla. */
  const [textoBuscado, setTextoBuscado] = useState('');

  const filtro: SeguimientoFdocFiltro = useMemo(() => ({
    estado,
    tipoDocumento,
    solicitanteOid,
    desde: periodo?.[0]?.startOf('day').toISOString(),
    hasta: periodo?.[1]?.endOf('day').toISOString(),
    texto: textoBuscado || undefined,
  }), [estado, tipoDocumento, solicitanteOid, periodo, textoBuscado]);

  const [recarga, setRecarga] = useState(0);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      setFilas(await getFirmas(filtro));
      // Obliga a las cronologías desplegadas a volver a pedirse: los eventos llegan
      // después, y una fila abierta se quedaría con lo que hubiera al abrirla.
      setRecarga((n) => n + 1);
    } catch {
      message.error('No se pudo cargar el seguimiento de documentos en firma');
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  // Opciones de los selects de tipo/responsable: se derivan de lo cargado, no de un catálogo aparte
  // (no existe uno hoy). Si el usuario ya filtró por uno, el otro selector se acota en consecuencia.
  const tiposDisponibles = useMemo(() => Array.from(
    new Map(filas.filter((f) => f.tipoDocumento).map((f) => [f.tipoDocumento as string, f.tipoDocumento as string])).values(),
  ).sort(), [filas]);

  const responsablesDisponibles = useMemo(() => Array.from(
    new Map(
      filas
        .filter((f) => f.solicitanteOid && f.solicitanteNombre)
        .map((f) => [f.solicitanteOid as string, f.solicitanteNombre as string]),
    ).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1])), [filas]);

  // Sin un sitio limpio hoy para preguntar el perfil del usuario en el front (no hay claims de rol
  // decodificados aquí, y duplicar `RolesCronologia`/`PuedeVerDetalleTecnico` del backend sería la
  // misma lista mantenida en dos sitios, lista para desincronizarse), la señal es el propio dato: si
  // el backend no manda `idSignaturit`, es que el usuario no es Sistemas y las tres columnas
  // técnicas no se montan. Con la tabla vacía no hay nada que ocultar, así que el valor por defecto
  // (columnas fuera) es el seguro.
  const esSistema = useMemo(() => filas.some((f) => f.idSignaturit != null), [filas]);

  const columnas: ColumnsType<FdocFirmaEstado> = [
    {
      title: 'Documento',
      dataIndex: 'idRegistro',
      width: 110,
      // Primera columna: es el identificador por el que se busca (ver el buscador de texto).
      render: (v: number) => <Text strong>#{v}</Text>,
    },
    {
      title: 'Fecha de envío',
      dataIndex: 'fechaEnvio',
      width: 160,
      render: (v: string) => formatear(v),
      sorter: (a, b) => (fechaUtc(a.fechaEnvio)?.valueOf() ?? 0) - (fechaUtc(b.fechaEnvio)?.valueOf() ?? 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Tipo de documento',
      dataIndex: 'tipoDocumento',
      width: 220,
      // «Presupuesto» a secas no dice qué se presupuestó: debajo va el servicio (PVA, PTF…).
      render: (v: string | null | undefined, fila) => (fila.servicioPresupuesto
        ? <>{v ?? '—'}<br /><Text type="secondary">{fila.codigoServicioPresupuesto} · {fila.servicioPresupuesto}</Text></>
        : v ?? '—'),
    },
    {
      title: 'Responsable',
      dataIndex: 'solicitanteNombre',
      width: 180,
      render: (v?: string | null) => v ?? '—',
    },
    {
      title: 'Firmas',
      width: 90,
      align: 'center',
      // Personas, no eventos: NU_FIRMANTES llega a 0 mientras no ha llegado ningún evento, y eso NO
      // es "cero firmantes" — es que aún no sabemos cuántos hay. Se pinta un guion, no "0 / 0".
      render: (_: unknown, fila) => (
        <Text type="secondary">
          {fila.numFirmantes > 0 ? `${fila.numFirmados} / ${fila.numFirmantes}` : '—'}
        </Text>
      ),
    },
    {
      title: 'Último evento',
      dataIndex: 'fechaUltimoEvento',
      width: 160,
      render: (v: string | null) => formatear(v),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 150,
      render: (v: string, fila) => (
        <FctEstadoTag
          estado={v}
          label={etiquetaEstado(v)}
          color={fila.incidencia ? 'red' : (COLOR_ESTADO[v] ?? 'default')}
        />
      ),
    },
    {
      title: 'Eventos',
      dataIndex: 'numEventos',
      width: 90,
      align: 'center',
    },
    // Detalle técnico (modo de firma, entorno, id de Signaturit): solo para el perfil de Sistemas.
    // No es solo un ocultado visual — el backend ni siquiera manda estos campos a quien no es
    // Sistemas (ver el comentario de `esSistema` más arriba), así que aquí basta con no montar la
    // columna cuando no hay nada que pintar.
    ...(esSistema ? [
      {
        title: 'Modo',
        dataIndex: 'modoFirma',
        width: 110,
        render: (v?: string | null) =>
          v === 'sequential' ? 'Secuencial' : v === 'parallel' ? 'Paralelo' : '—',
      },
      {
        title: 'Entorno',
        dataIndex: 'entorno',
        width: 110,
        render: (v?: string | null) => (v === 'produccion' ? 'Producción' : v === 'sandbox' ? 'Sandbox' : '—'),
      },
      {
        title: 'Signaturit',
        dataIndex: 'idSignaturit',
        ellipsis: true,
        render: (v?: string | null) => (v ? <Text type="secondary" copyable={{ text: v }}>{v}</Text> : '—'),
      },
    ] as ColumnsType<FdocFirmaEstado> : []),
  ];

  return (
    <div style={{ padding: '0 8px' }}>
      <FctCard accent={false}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0',
        }}>
          <Space>
            <DashboardOutlined />
            <Title level={4} style={{ margin: 0 }}>Seguimiento de documentos en firma digital</Title>
          </Space>
        </div>

        <FctListHeader
          leftContent={
            <>
              <Select
                allowClear
                placeholder="Estado"
                style={{ width: 180 }}
                value={estado}
                onChange={setEstado}
                options={[
                  ...ESTADOS_RECORRIDO.map(e => ({ value: e, label: etiquetaEstado(e) })),
                  ...Object.entries(ETIQUETAS_INCIDENCIA).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
              <Select
                allowClear
                placeholder="Tipo de documento"
                style={{ width: 200 }}
                value={tipoDocumento}
                onChange={setTipoDocumento}
                options={tiposDisponibles.map((t) => ({ value: t, label: t }))}
              />
              <Select
                allowClear
                placeholder="Responsable"
                style={{ width: 200 }}
                value={solicitanteOid}
                onChange={setSolicitanteOid}
                options={responsablesDisponibles.map(([oid, nombre]) => ({ value: oid, label: nombre }))}
              />
              <RangePicker
                format="DD/MM/YYYY"
                placeholder={['Desde', 'Hasta']}
                value={periodo}
                onChange={(v) => setPeriodo(v as [Dayjs | null, Dayjs | null] | null)}
              />
            </>
          }
        >
          <Tooltip title="La vista solo permite buscar por nº de documento o id de Signaturit">
            <Input
              placeholder="Nº de documento o id de Signaturit"
              prefix={<SearchOutlined />}
              style={{ width: 280 }}
              allowClear
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                if (!e.target.value) setTextoBuscado('');
              }}
              onPressEnter={() => setTextoBuscado(texto.trim())}
            />
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={cargar} loading={loading}>Actualizar</Button>
        </FctListHeader>

        <Table<FdocFirmaEstado>
          // `idRegistro` es el único identificador que llega siempre, lo vea quien lo vea —
          // `idSignaturit` ahora es null para quien no es Sistemas (ver `esSistema` arriba).
          rowKey="idRegistro"
          columns={columnas}
          dataSource={filas}
          // Solo el velo de carga de la PRIMERA vez: al refrescar con datos ya en pantalla
          // se deja la tabla como está y el aviso va en el botón. Si no, cada Actualizar
          // hacía parpadear la tabla entera y se perdía de vista lo que estabas mirando.
          loading={loading && filas.length === 0}
          size="small"
          scroll={{ x: 1400 }}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} documentos` }}
          expandable={{
            expandedRowRender: (fila) => (
              <CronologiaFila idRegistro={fila.idRegistro} recarga={recarga} />
            ),
          }}
        />
      </FctCard>
    </div>
  );
};

export default SeguimientoFDocPage;
